import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePortfolios } from '@/hooks/usePortfolios';
import { EmptyState } from '@/lib/base/empty-state/empty-state';
import { Select } from '@/lib/base/forms/select/select';
import type { SelectItemType } from '@/lib/base/forms/select/select-shared';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import HorizontalTabs from '@/lib/base/tabs/HorizontalTabs';
import { OrgMembersSection } from '@/lib/portfolio/components/OrgMembersSection';
import { ScopeBand } from '@/lib/portfolio/components/ScopeBand';
import { SnowflakeConnectSection } from '@/lib/portfolio/components/SnowflakeConnectSection';
import { SnowflakeCredentialSection } from '@/lib/portfolio/components/SnowflakeCredentialSection';

export function PortfolioPage() {
  const { data: portfolios, isLoading } = usePortfolios();
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<number | null>(
    null
  );
  // Selected org tab within the current portfolio. Falls back to the first org
  // when unset or when a portfolio switch leaves it pointing at an org that is
  // not in the newly selected portfolio.
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedPortfolioId === null && portfolios && portfolios.length > 0) {
      setSelectedPortfolioId(portfolios[0]?.id ?? null);
    }
  }, [portfolios, selectedPortfolioId]);

  const selected = useMemo(
    () =>
      portfolios?.find(p => p.id === selectedPortfolioId) ?? portfolios?.[0],
    [portfolios, selectedPortfolioId]
  );

  const portfolioItems = useMemo<SelectItemType[]>(
    () => (portfolios ?? []).map(p => ({ id: String(p.id), label: p.name })),
    [portfolios]
  );

  const handlePortfolioSelectionChange = useCallback(
    (key: string | number | null) => {
      if (key === null || key === undefined) {
        return;
      }
      setSelectedPortfolioId(Number(key));
      setSelectedOrgId(null);
    },
    []
  );

  // One org shown at a time: the tab bar is the navigation between the
  // portfolio's orgs (HorizontalTabs hides itself for single-org portfolios).
  const orgTabs = useMemo(
    () =>
      (selected?.orgs ?? []).map(o => ({
        id: String(o.org_id),
        label: o.name,
      })),
    [selected]
  );

  const activeOrg = useMemo(() => {
    const orgs = selected?.orgs ?? [];
    return orgs.find(o => String(o.org_id) === selectedOrgId) ?? orgs[0];
  }, [selected, selectedOrgId]);

  const handleOrgTabChange = useCallback((tabId: string) => {
    setSelectedOrgId(tabId);
  }, []);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-dvh'>
        <LoadingIndicator size='md' />
      </div>
    );
  }

  return (
    <div className='max-w-5xl mx-auto px-4 py-8 sm:px-6 lg:px-8'>
      <div className='flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between mb-8'>
        <div>
          {/* Without this the page opens on an unlabelled H1 sitting above two
              different scopes, naming neither of them. */}
          <p className='text-caption uppercase tracking-wide text-[var(--color-text-tertiary)]'>
            Portfolio
          </p>
          <h1 className='text-heading-2 text-[var(--color-text-primary)]'>
            {selected?.name ?? 'Portfolio'}
          </h1>
        </div>

        {portfolios && portfolios.length > 1 && (
          <Select
            aria-label='Select portfolio'
            size='sm'
            className='sm:w-64'
            items={portfolioItems}
            selectedKey={selected ? String(selected.id) : null}
            onSelectionChange={handlePortfolioSelectionChange}
          >
            {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
          </Select>
        )}
      </div>

      {/* Portfolio-wide blocks, then the per-org blocks, each under a band that
          says which scope it is. Connector credentials sits second because it is
          the most technical thing here and shouldn't head the page — it used to
          render above the org tabs, which made a portfolio-wide table read as
          though it belonged to whichever org was selected. */}
      {selected && (
        <>
          <ScopeBand label='Across all organizations' />
          {selected.orgs.length > 0 && (
            <div className='mb-8'>
              <SnowflakeConnectSection
                portfolioId={selected.id}
                orgs={selected.orgs}
              />
            </div>
          )}
          <SnowflakeCredentialSection
            key={selected.id}
            portfolioId={selected.id}
          />
        </>
      )}

      {selected && selected.orgs.length > 0 && activeOrg && (
        <>
          <ScopeBand label='One organization at a time' />
          <div className='flex flex-col gap-6'>
            <HorizontalTabs
              tabs={orgTabs}
              activeTab={String(activeOrg.org_id)}
              onTabChange={handleOrgTabChange}
              idPrefix='portfolio-org'
              lazyPanels
            />
            {/* Only the active org's panel is mounted, so it carries the tabpanel
                role and is labelled by its tab (id matches HorizontalTabs'
                `${idPrefix}-panel-${id}` / `${idPrefix}-${id}` convention). This
                keeps the tab↔panel relationship valid for screen readers. */}
            <div
              role='tabpanel'
              id={`portfolio-org-panel-${activeOrg.org_id}`}
              aria-labelledby={`portfolio-org-${activeOrg.org_id}`}
            >
              <OrgMembersSection
                key={activeOrg.org_id}
                portfolioId={selected.id}
                orgId={activeOrg.org_id}
                orgName={activeOrg.name}
              />
            </div>
          </div>
        </>
      )}
      {selected && selected.orgs.length === 0 && (
        <EmptyState
          variant='empty'
          title='No organizations linked'
          description='This portfolio has no linked organizations yet.'
        />
      )}
      {!selected && <EmptyState variant='empty' title='No portfolios' />}
    </div>
  );
}

export default PortfolioPage;
